import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import catalogApi from '../lib/catalogApi';
import type {
  CatalogResponse,
  Product,
  ProductDetail,
  InventoryLot,
  CreateProductInput,
  UpdateProductInput,
  StockAdjustmentInput,
} from '../lib/catalogApi';
import { useNotifications } from '../stores/uiStore';

export const inventoryKeys = {
  all: ['inventory'] as const,
  lists: () => [...inventoryKeys.all, 'list'] as const,
  list: (filters: InventoryFilters) => [...inventoryKeys.lists(), filters] as const,
  details: () => [...inventoryKeys.all, 'detail'] as const,
  detail: (id: string) => [...inventoryKeys.details(), id] as const,
  lots: () => [...inventoryKeys.all, 'lots'] as const,
  lotsByProduct: (productId: string) => [...inventoryKeys.lots(), productId] as const,
  allLots: (filters: LotFilters) => [...inventoryKeys.lots(), 'all', filters] as const,
};

export interface InventoryFilters {
  page?: number;
  pageSize?: number;
  category?: string;
  zone?: string;
  search?: string;
  lowStock?: boolean;
}

export interface LotFilters {
  expiringWithin?: number;
  search?: string;
}

export function useInventory(
  filters: InventoryFilters = {}
): UseQueryResult<CatalogResponse<Product[]>> {
  return useQuery({
    queryKey: inventoryKeys.list(filters),
    queryFn: () => catalogApi.getAll(filters).then((res) => res.data),
  });
}

export function useProduct(id: string): UseQueryResult<CatalogResponse<ProductDetail>> {
  return useQuery({
    queryKey: inventoryKeys.detail(id),
    queryFn: () => catalogApi.getById(id).then((res) => res.data),
    enabled: Boolean(id),
  });
}

export function useProductLots(
  productId: string
): UseQueryResult<CatalogResponse<InventoryLot[]>> {
  return useQuery({
    queryKey: inventoryKeys.lotsByProduct(productId),
    queryFn: () => catalogApi.getLots(productId).then((res) => res.data),
    enabled: Boolean(productId),
  });
}

export function useAllLots(
  filters: LotFilters = {}
): UseQueryResult<CatalogResponse<InventoryLot[]>> {
  return useQuery({
    queryKey: inventoryKeys.allLots(filters),
    queryFn: () => catalogApi.getAllLots(filters).then((res) => res.data),
  });
}

export function useCreateProduct() {
  const queryClient = useQueryClient();
  const { add: addNotification } = useNotifications();

  return useMutation({
    mutationFn: (data: CreateProductInput) => catalogApi.create(data).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: inventoryKeys.lists() });
      addNotification({
        type: 'success',
        title: 'Product created',
        message: 'The product is now available in the catalog.',
      });
    },
    onError: (error: Error) => {
      addNotification({ type: 'error', title: 'Product creation failed', message: error.message });
    },
  });
}

export function useUpdateProduct() {
  const queryClient = useQueryClient();
  const { add: addNotification } = useNotifications();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateProductInput }) =>
      catalogApi.update(id, data).then((res) => res.data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: inventoryKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: inventoryKeys.lists() });
      addNotification({
        type: 'success',
        title: 'Product updated',
        message: 'The product master was updated successfully.',
      });
    },
    onError: (error: Error) => {
      addNotification({ type: 'error', title: 'Product update failed', message: error.message });
    },
  });
}

export function useAdjustStock() {
  const queryClient = useQueryClient();
  const { add: addNotification } = useNotifications();

  return useMutation({
    mutationFn: ({ id, adjustment }: { id: string; adjustment: StockAdjustmentInput }) =>
      catalogApi.adjustStock(id, adjustment).then((res) => res.data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: inventoryKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: inventoryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: inventoryKeys.lotsByProduct(id) });
      addNotification({
        type: 'success',
        title: 'Stock adjusted',
        message: 'The inventory ledger was updated successfully.',
      });
    },
    onError: (error: Error) => {
      addNotification({ type: 'error', title: 'Stock adjustment failed', message: error.message });
    },
  });
}

export function usePrefetchProduct() {
  const queryClient = useQueryClient();
  return (id: string) => {
    void queryClient.prefetchQuery({
      queryKey: inventoryKeys.detail(id),
      queryFn: () => catalogApi.getById(id).then((res) => res.data),
    });
  };
}

export function useLowStockProducts(): UseQueryResult<CatalogResponse<Product[]>> {
  return useQuery({
    queryKey: [...inventoryKeys.lists(), 'lowStock'],
    queryFn: () => catalogApi.getAll({ lowStock: true }).then((res) => res.data),
  });
}

export function useExpiringLots(
  daysWithin: number = 7
): UseQueryResult<CatalogResponse<InventoryLot[]>> {
  return useQuery({
    queryKey: inventoryKeys.allLots({ expiringWithin: daysWithin }),
    queryFn: () => catalogApi.getAllLots({ expiringWithin: daysWithin }).then((res) => res.data),
  });
}

export type {
  Product,
  ProductDetail,
  InventoryLot,
  CreateProductInput,
  UpdateProductInput,
  StockAdjustmentInput,
};

export default {
  useInventory,
  useProduct,
  useProductLots,
  useAllLots,
  useCreateProduct,
  useUpdateProduct,
  useAdjustStock,
  usePrefetchProduct,
  useLowStockProducts,
  useExpiringLots,
};
